import { Component, inject, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { SearchComponent } from '../../shared/search/search.component';
import { PaginationComponent } from '../../components/pagination/pagination.component';
import { SuccessComponent } from '../../components/card/success/success.component';
import { ProgressLoaderComponent } from '../../components/loaders/progress-loader/progress-loader.component';
import { ImagesService } from '../../services/general/images.service';
import { CategoryService } from '../../services/categories/category.service';
import { Category, CategoryQueryParams } from '../../interfaces/responses/category.interface';
import { PaginationInterface } from '../../interfaces/responses/pagination.interface';
import { CreateNewCategoryComponent } from '../../components/card/category/create-new-category.component';
import { CategoryTableComponent } from '../../components/card/category/category-table/category-table.component';
import { ToastService } from '../../services/general/toast.service';
import { PermissionService } from '../../services/permission/permission.service';


@Component({
  selector: 'app-categories',
  imports: [
    PaginationComponent,
    CreateNewCategoryComponent,
    SuccessComponent,
    CommonModule,
    ReactiveFormsModule,
    ProgressLoaderComponent,
    CategoryTableComponent
  ],
  templateUrl: './categories.component.html',
  styleUrl: './categories.component.scss'
})
export class CategoriesComponent {
  // Injecting required services
  _images = inject(ImagesService);
  permissionService = inject(PermissionService);
  categoryService = inject(CategoryService);
  _fb = inject(FormBuilder);
  searchQuery!: FormGroup;
  platformId = inject(PLATFORM_ID);
  isLoading: boolean = false;
  viewMode: 'table' | 'grid' = 'table';
  categoryQueryParams: CategoryQueryParams = {
    keyword: '',
    date: '',
    isActive: null,
    productId: '',
    paginate: true,
    perPage: 10,
    page: 1
  }

  // UI control flags
  isCreateApplication: boolean = false;
  isUpdateCategory: boolean = false;
  showSuccess: boolean = false;
  _toast = inject(ToastService);

  // Pagination and data variables
  allCategories: PaginationInterface<Category[]> = { data: [], meta: { page: 1, perPage: 10, total: 6, totalPages: 1 } };
  categories: Category[] = [];
  category: Category | null = null;

  /**
   * Lifecycle hook: called once the component is initialized
   */
  ngOnInit(): void {
    this.getCategories();

    // search Query
    this.searchQuery = this._fb.group({
      name: [''],
    });

    if (isPlatformBrowser(this.platformId)) {
      this.searchQuery.get('name')?.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((query: string) => {
          this.categoryQueryParams.keyword = query.trim();
          this.getCategories();
        })
    }
  }

  /**
   * Fetches paginated categories from the server and updates the category list
   */
  getCategories(): void {
    this.isLoading = true;
    this.categoryService.fetchCategories(this.categoryQueryParams).subscribe({
      next: (response) => {
        this.allCategories = response;
        this.categories = response.data || [];
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error fetching categories:', err);
        this.isLoading = false;
      }
    });
  }

  /**
   * Updates the current page when pagination changes
   */
  onPageChange(page: number): void {
    this.categoryQueryParams.page = page;
    this.getCategories(); // Re-fetch data for the new page
  }

  onCreateApplication(): void {
    this.isCreateApplication = true;
  }

  onCloseApplication(): void {
    this.category = null;
    this.isCreateApplication = false;
  }

  onShowOverlay() {
    if (this.category) {
      this.isUpdateCategory = true;
    }
  }

  onUpdateCategory(category: Category) {
    this.category = category;
  }

  // Close handler specifically for the update overlay
  onCloseUpdateCategory() {
    this.isUpdateCategory = false;
    this.category = null;
  }

}
